import Vue from 'vue';
import { certificateService } from "../services/certificate.service";

const state = {
  certificates: {}, 
  userCertificates: [], 
  nextPageToken: null,
  pageSize: 10,
  loading: false,
  reloading: false,
  error: null
};

const getters = {
  allCertificates: state => Object.values(state.certificates),
  certificateById: state => id => state.certificates[id],
  userCertificates: state => state.userCertificates,
  isLoading: state => state.loading,
  isReloading: state => state.reloading,
  hasMore: state => state.nextPageToken,
  certificateError: state => state.error
};

const actions = {

  async fetchCertificates({ commit }) {
    commit("setLoading", true);
    try {
      const certificates = await certificateService.fetchCertificates();
      commit("setCertificates", certificates);
      return certificates;
    } catch (error) {
      commit("setError", error);
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchCertificateByID({ commit }, id) {
    commit("setLoading", true);
    try {
      const certificate = await certificateService.fetchCertificateByID(id);
      commit("setCertificate", certificate);
      return certificate;
    } catch (error) {
      commit("setError", error);
    } finally {
      commit("setLoading", false);
    }
  },

  async addCertificate({ commit }, certificateData) {
    commit("setLoading", true);
    try {
      const newCertificate = await certificateService.addCertificate(certificateData);
      commit("setCertificate", newCertificate);
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },

  async updateCertificate({ commit }, certificateData) {
    commit("setLoading", true);
    try {
      const updatedCertificate = await certificateService.updateCertificate(certificateData);
      commit("updateCertificate", updatedCertificate);
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },

  async deleteCertificate({ commit }, id) {
    commit("setLoading", true);
    try {
      const isDeleted = await certificateService.deleteCertificate(id);
      if(isDeleted) commit("removeCertificate", id);
      return isDeleted;
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchCurrentUserCertificates({ commit }) {
    commit("setLoading", true);
    try {
      const certificates = await certificateService.fetchCurrentUserCertificates()
      commit("setUserCertificates", certificates); 
      return certificates;
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchUserCertificates({ commit, state }, { searchQuery = null, isNewestFirst = true, certificateIds = null, startDate = null, endDate = null, reload = false } = {}) {
    if (state.loading) return;
  
    commit("setLoading", true);

    if (reload)  { 
      commit("setReloading", true); 
      commit("setUserCertificates", []); 
    }
  
    try {
      const requestParams = { 
        searchQuery,
        pageSize: state.pageSize,
        nextPageToken: reload ? null : state.nextPageToken,
        isNewestFirst: isNewestFirst,
        certificateIds: certificateIds,
        startDate: startDate,
        endDate: endDate
      };
      
      const responseData = await certificateService.fetchUserCertificates(requestParams);
      const payload = responseData?.payload || [];
      const nextToken = responseData?.nextPageToken || null;
  
      if (reload) {
        commit("setUserCertificates", payload); 
        commit("setReloading", false);
      } else {
        commit("appendUserCertificates", payload); 
      }
  
      commit("setNextPageToken", nextToken);
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },

  async loadMoreUserCertificates({ dispatch, state }, { isNewestFirst = true, searchQuery = null, certificateIds = null, startDate = null, endDate = null } = {}) {
    if (!state.nextPageToken || state.loading) return; 
  
    return await dispatch("fetchUserCertificates", { searchQuery, isNewestFirst, certificateIds, startDate, endDate, reload: false });
  },

  async revokeUserCertificate({ commit }, id) {
    commit("setLoading", true);
    try {
      const updatedCertificate = await certificateService.revokeUserCertificate(id)
      const isRevoked = updatedCertificate?.isRevoked || false;

      if(isRevoked) commit("updateUserCertificate", updatedCertificate); 

      return isRevoked;
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },

  async exportUserCertificates({ commit }, { searchQuery = null, certificateIds = null, startDate = null, endDate = null } = {}) {
    commit("setLoading", true);
    try {
      await certificateService.exportUserCertificates({ searchQuery, certificateIds, startDate, endDate })
    } catch (error) {
      commit("setError", error);
      return Promise.reject(error)
    } finally {
      commit("setLoading", false);
    }
  },
};


const mutations = {
  setCertificates(state, certificates) {
    const certificatesMap = certificates.reduce((acc, certificate) => {
      acc[certificate.id] = certificate;
      return acc;
    }, {});
    Vue.set(state, 'certificates', certificatesMap);
  },

  setCertificate(state, certificate) {
    Vue.set(state.certificates, certificate.id, certificate);
  },

  updateCertificate(state, updatedCertificate) {
    if (state.certificates[updatedCertificate.id]) {
      Vue.set(state.certificates, updatedCertificate.id, updatedCertificate);
    }
  },

  removeCertificate(state, id) {
    Vue.delete(state.certificates, id);
  },

  setUserCertificates(state, userCertificates) {
    Vue.set(state, 'userCertificates', userCertificates);
  },

  updateUserCertificate(state, userCertificate) {
    const index = state.userCertificates.findIndex((detail) => detail.userCertificate.id === userCertificate.id);
    const updatedCertificate = { ...state.userCertificates[index], userCertificate }

    if (index !== -1) {
      Vue.set(state.userCertificates, index, updatedCertificate);
    }
  },

  appendUserCertificates(state, certificates) {
    const updatedCertificates = [...state.userCertificates, ...certificates];
    Vue.set(state, 'userCertificates', updatedCertificates);
  },

  setLoading(state, isLoading) {
    Vue.set(state, 'loading', isLoading);
  },

  setReloading(state, isReloading) {
    Vue.set(state, 'reloading', isReloading);
  },

  setNextPageToken(state, token) {
    Vue.set(state, 'nextPageToken', token);
  },

  setError(state, error) {
    Vue.set(state, 'error', error);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};